import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, useReducedMotion } from 'framer-motion'
import {
  ArrowDown,
  BadgeCheck,
  CalendarDays,
  Check,
  CreditCard,
  FileText,
  GalleryHorizontalEnd,
  Globe2,
  Handshake,
  MessageCircle,
  Search,
  ShieldCheck,
  Ticket,
  Users,
} from 'lucide-react'
import Footer from '../components/Footer'
import { Button } from '../components/ui/button'
import { trackEvent } from '../utils/mixpanel'

const BACKGROUND_PATTERN_URL =
  'https://images.unsplash.com/photo-1737276745810-ce753d468a34?auto=format&fit=crop&w=2200&q=80'

const TRUST_MARKERS = [
  'Join 100+ facilitators',
  'Verified Trust profiles',
  'Discovery, ticketing, and client logistics handled',
]

const PLATFORM_BENEFITS = [
  {
    icon: FileText,
    title: 'Zero Admin & Client Logistics',
    description:
      'From payments and automated ticketing to pre-event communication and attendee queries, we manage the operational burden so you can guide the session.',
  },
  {
    icon: Search,
    title: 'Get Discovered',
    description:
      'Tap into smart matching that connects your modalities with seekers actively looking for exactly what you offer.',
  },
  {
    icon: Globe2,
    title: 'Stop the App-Juggling',
    description:
      'Replace scattered link trees, Google Forms, payment links, and social messages with one professional, high-converting platform.',
  },
  {
    icon: Handshake,
    title: 'Keep Your Independence',
    description:
      'Run workshops, retreats, and 1-on-1s on your terms, with transparent payouts and no hidden operational surprises.',
  },
]

const DIGITAL_HOME_FEATURES = [
  {
    icon: BadgeCheck,
    title: 'The Complete Bio',
    description: 'Tell your story, highlight certifications, and define your unique approach.',
  },
  {
    icon: GalleryHorizontalEnd,
    title: 'Dynamic Portfolio',
    description: 'Showcase past workshops, retreats, and media to build credibility with new seekers.',
  },
  {
    icon: MessageCircle,
    title: 'Verified Feedback',
    description: 'Consolidate real seeker testimonials so trust builds faster than social media likes.',
  },
]

const LISTING_FEATURES = [
  'Online sessions',
  'Small group circles and workshops',
  'Multi-day retreats and offsites',
  'Built-in promotion to the Wellness Loop community',
]

const JOIN_STEPS = [
  {
    step: 'Step 1',
    title: 'Apply & Verify',
    description:
      'Submit your credentials for the Verified Trust badge. Our team reviews applications within 24-48 hours.',
  },
  {
    step: 'Step 2',
    title: 'Build Your Space',
    description: 'Set up your holistic profile, add your bio, and build your digital portfolio.',
  },
  {
    step: 'Step 3',
    title: 'Launch Your Offerings',
    description: 'Publish your first event, class, or retreat and let the Wellness Loop community find you.',
  },
]

const FacilitatorLanding = () => {
  const shouldReduceMotion = useReducedMotion()

  useEffect(() => {
    trackEvent('Page Viewed', { page: 'Facilitators Landing' })
  }, [])

  const scrollToDetails = (event) => {
    event?.preventDefault()
    const target = document.getElementById('facilitator-platform')
    if (!target) return

    const top = target.getBoundingClientRect().top + window.scrollY
    window.scrollTo({
      top,
      behavior: shouldReduceMotion ? 'auto' : 'smooth',
    })
  }

  return (
    <main className="relative min-h-screen overflow-x-hidden bg-primary-300">
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 opacity-100"
        style={{
          backgroundImage: `url(${BACKGROUND_PATTERN_URL})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.78 }}
        transition={{ duration: 20, ease: 'easeOut', repeat: Infinity, repeatType: 'reverse' }}
      />
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-br from-cream/90 via-cream/84 to-primary-50/78"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2, ease: 'easeOut' }}
      />

      <section className="relative z-10 overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <motion.div
            className="absolute -left-20 -top-24 h-56 w-56 rounded-full bg-cream/50 blur-3xl sm:h-72 sm:w-72 lg:h-96 lg:w-96"
            animate={
              shouldReduceMotion ? undefined : { x: [0, 34, 0], y: [0, 38, 0], scale: [1, 1.1, 1] }
            }
            transition={{ duration: 16, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute right-0 top-1/3 h-48 w-48 rounded-full bg-cream/80 blur-3xl sm:h-64 sm:w-64 lg:h-80 lg:w-80"
            animate={
              shouldReduceMotion ? undefined : { x: [-220, -80, 0], y: [0, -40, 0], scale: [1, 0.9, 1] }
            }
            transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse', ease: 'easeInOut' }}
          />
        </div>

        <div className="relative mx-auto grid min-h-screen max-w-7xl items-start gap-8 px-5 pb-10 pt-20 sm:px-8 sm:pb-16 sm:pt-24 lg:grid-cols-2 lg:items-center lg:gap-14 lg:px-12 lg:py-20">
          <motion.a
            href="/"
            className="absolute inset-x-0 top-6 flex justify-center sm:top-7 lg:top-8"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            aria-label="Wellness Loop home"
          >
            <img src="/logo.png" alt="Wellness Loop Logo" className="h-12 w-auto sm:h-20" />
          </motion.a>

          <motion.div
            className="order-2 lg:order-1"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
          >
            <motion.div
              animate={shouldReduceMotion ? undefined : { y: [0, -4, 0] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img
                src="/fasc-lander-hero.jpg"
                alt="Facilitator on Wellness Loop"
                className="h-[300px] w-full rounded-3xl border border-primary-100/80 object-cover object-top shadow-xl sm:h-[400px] lg:h-[560px]"
              />
            </motion.div>
          </motion.div>

          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.25, ease: 'easeOut' }}
          >
            <motion.p
              className="mb-4 inline-flex rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700 sm:text-sm"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: 'easeOut' }}
            >
              Wellness Loop For Facilitators
            </motion.p>

            <motion.h1
              className="max-w-xl text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: 'easeOut' }}
            >
              Focus on Facilitating. We&apos;ll Handle the Rest.
            </motion.h1>
            <motion.p
              className="mt-4 max-w-xl text-base leading-relaxed text-gray-700 sm:text-xl"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: 'easeOut' }}
            >
              Build your digital home, connect with active wellness seekers, and let Wellness Loop handle discovery,
              ticketing, payments, and client logistics.
            </motion.p>
            <div className="mt-4 flex flex-col gap-2 text-sm text-gray-700 sm:grid-cols-2">
                {TRUST_MARKERS.map((marker) => (
                  <span key={marker} className="text-lg inline-flex items-center gap-2">
                    <Check className="h-4 w-4 shrink-0 text-primary-700" aria-hidden="true" />
                    {marker}
                  </span>
             
                ))}  
              </div>
            <motion.div
              className="mt-6 flex flex-col gap-4 sm:mt-8"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.85, ease: 'easeOut' }}
            >
              <div className="flex flex-col gap-3 sm:flex-col">
                <Button
                  asChild
                  size="lg"
                  className="rounded-full bg-primary-700 px-8 py-6 text-sm font-semibold text-white shadow-lg shadow-primary-700/25 transition-colors hover:bg-primary-800 sm:text-base"
                >
                  <Link to="/facilitators/register">Apply to Become a Verified Facilitator</Link>
                </Button>
                <a
                  href="#facilitator-platform"
                  onClick={scrollToDetails}
                  className="group inline-flex items-center justify-center gap-2 border-0 bg-transparent p-0 text-center text-sm font-semibold text-primary-700 transition-colors duration-200 hover:text-primary-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-700 focus-visible:ring-offset-2 sm:text-base"
                >
                  <span className="underline-offset-4 group-hover:underline">Know more</span>
                  <ArrowDown className="h-4 w-4 transition-transform duration-200 group-hover:translate-y-0.5" aria-hidden="true" />
                </a>
              </div>
              
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section
        id="facilitator-platform"
        className="relative z-10 scroll-mt-20 border-t border-primary-100/60 bg-primary-900/90"
      >
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <motion.div
            className="mx-auto max-w-4xl text-center"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200/40 bg-primary-800/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-cream sm:text-sm">
              Why Build Your Practice on Wellness Loop?
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-cream sm:text-4xl lg:text-5xl">
              You guide the experience. We remove the operational drag.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-primary-100 sm:text-lg">
              You didn&apos;t become a facilitator to juggle spreadsheets, payment links, and WhatsApp groups.
              Wellness Loop gives your practice a cleaner system for discovery, trust, bookings, and follow-through.
            </p>
          </motion.div>

          <div className="mt-12 grid gap-5 md:grid-cols-2">
            {PLATFORM_BENEFITS.map((benefit, index) => {
              const Icon = benefit.icon
              return (
                <motion.article
                  key={benefit.title}
                  className="rounded-3xl border border-primary-100/25 bg-primary-800/70 p-6 shadow-xl sm:p-7"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.18 }}
                  transition={{ duration: 0.55, delay: index * 0.06, ease: 'easeOut' }}
                >
                  <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-cream text-primary-800">
                    <Icon className="h-6 w-6" aria-hidden="true" />
                  </div>
                  <h3 className="text-xl font-semibold text-cream sm:text-2xl">{benefit.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-primary-100 sm:text-base">{benefit.description}</p>
                </motion.article>
              )
            })}
          </div>
        </div>
      </section>

      <section className="relative z-10 border-t border-primary-100/60 bg-cream/90">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:px-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <img
              src="/lisha-sample-profile.jpg"
              alt="Sample facilitator profile on Wellness Loop"
              className="w-full rounded-3xl border border-primary-100/80 object-cover object-top shadow-xl p-1"
            />
            <p className="mt-4 text-sm text-gray-600 text-center">Your profile on Wellness Loop</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700 sm:text-sm">
              Your New Digital Home
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              Everything clients need to trust you, consolidated in one beautiful space.
            </h2>
            <div className="mt-8 space-y-4">
              {DIGITAL_HOME_FEATURES.map((feature) => {
                const Icon = feature.icon
                return (
                  <div key={feature.title} className="rounded-2xl border border-primary-100/80 bg-white/70 p-5 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-700 text-white">
                        <Icon className="h-5 w-5" aria-hidden="true" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-gray-700 sm:text-base">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 border-t border-primary-100/60 bg-[#f4f6f8]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200 bg-primary-50 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary-700 sm:text-sm">
              Frictionless Event & Retreat Listings
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-gray-900 sm:text-4xl lg:text-5xl">
              From intimate workshops to immersive offsites, list it in minutes.
            </h2>
            <p className="mt-5 text-base leading-relaxed text-gray-700 sm:text-lg">
              No matter the size or scale of your event - our listing tools handle ticketing, capacity, reminders, and promotion to the right seekers.
            </p>
            {/* <div className="mt-7 grid gap-3 sm:grid-cols-2">
              {LISTING_FEATURES.map((feature) => (
                <div key={feature} className="flex items-start gap-3 rounded-2xl border border-primary-100 bg-white/75 p-4">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-primary-700" aria-hidden="true" />
                  <span className="text-sm font-medium text-gray-800">{feature}</span>
                </div>
              ))}
            </div> */}
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {[
                { icon: Ticket, label: 'Automated ticketing' },
                { icon: CreditCard, label: 'Payments managed' },
                { icon: CalendarDays, label: 'Capacity control' },
                { icon: Users, label: 'Built-in promotion to the Wellness Loop community' },
              ].map((item) => {
                const Icon = item.icon
                return (
                  <div key={item.label} className="flex flex-row  items-center rounded-2xl border border-primary-100 bg-primary-50/70 p-4">
                    <Icon className="h-5 w-5 text-primary-700 mr-2" aria-hidden="true" />
                    <p className="text-sm font-semibold text-primary-900">{item.label}</p>
                  </div>
                )
              })}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <img
              src="/fasc-lander-events.jpg"
              alt="Facilitator event listing on Wellness Loop"
              width={1252}
              height={832}
              className="w-full h-auto rounded-3xl border border-primary-100/80 shadow-xl"
            />
          </motion.div>
        </div>
      </section>

      <section className="relative z-10 overflow-hidden border-t border-primary-100/60 bg-primary-900">
        <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8 lg:px-12 lg:py-20">
          <motion.div
            className="mx-auto max-w-4xl text-center"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65, ease: 'easeOut' }}
          >
            <p className="mb-4 inline-flex rounded-full border border-primary-200/40 bg-primary-800/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.14em] text-cream sm:text-sm">
              How to Join the Loop
            </p>
            <h2 className="text-3xl font-semibold leading-tight text-cream sm:text-4xl lg:text-5xl">
              A simple path to growing your practice.
            </h2>
          </motion.div>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            {JOIN_STEPS.map((item, index) => (
              <motion.article
                key={item.title}
                className="rounded-3xl border border-primary-100/25 bg-primary-800/75 p-6 shadow-xl sm:p-7"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.18 }}
                transition={{ duration: 0.55, delay: index * 0.08, ease: 'easeOut' }}
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-200">{item.step}</p>
                <h3 className="mt-3 text-2xl font-semibold text-cream">{item.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-primary-100 sm:text-base">{item.description}</p>
              </motion.article>
            ))}
          </div>

          <motion.div
            className="mt-12 rounded-3xl border border-primary-100/25 bg-cream p-6 text-center shadow-xl sm:p-8"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.18 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <ShieldCheck className="mx-auto h-10 w-10 text-primary-700" aria-hidden="true" />
            <h2 className="mt-4 text-2xl font-semibold text-primary-900 sm:text-3xl">
              Start with the Verified Trust application.
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-gray-700 sm:text-base">
              Build your space, launch your first offering, and let Wellness Loop bring the right seekers to your work.
            </p>
            <Button
              asChild
              size="lg"
              className="mt-6 rounded-full bg-primary-700 px-8 py-6 text-sm font-semibold text-white shadow-lg shadow-primary-700/25 transition-colors hover:bg-primary-800 sm:text-base"
            >
              <Link to="/facilitators/register">Start Your Application</Link>
            </Button>
          </motion.div>
        </div>
      </section>

      <div className="relative z-10">
        <Footer />
      </div>
    </main>
  )
}

export default FacilitatorLanding
